import { useTranslation } from '@rhino-project/core/i18n';
import { useCallback } from 'react';
import type { Key } from 'react';

import { useModelIndex, useModelUpdate } from '@rhino-project/core/hooks';
import { useBaseOwnerId, useHasRoleOf } from '@rhino-project/core/hooks';
import type { RhinoRead } from '@rhino-project/core';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectIndicator,
  SelectPopover,
  ListBox,
  ListBoxItem
} from '@heroui/react';

type PlayerRosterStatusProps = {
  roster: RhinoRead<'roster'>;
};

const PlayerRosterStatus = ({ roster }: PlayerRosterStatusProps) => {
  const { t } = useTranslation();

  const organization = useBaseOwnerId();
  const isAdmin = useHasRoleOf('admin');

  const { results } = useModelIndex('roster_status', {
    filter: {
      organization,
      status_type: [roster.team_season.status, 'archived']
    },
    order: ['order']
  });
  const { mutate: updateRoster } = useModelUpdate('roster');

  const handleRosterStatusChange = useCallback(
    (key: Key | null, r: RhinoRead<'roster'>) => {
      if (!key) return;
      updateRoster({
        id: r.id,
        roster_status: String(key)
      } as unknown as Parameters<typeof updateRoster>[0]);
    },
    [updateRoster]
  );

  const isArchived = roster.team_season.status === 'archived';

  if (!isAdmin || isArchived) {
    return <div>{roster.roster_status.name}</div>;
  }

  return (
    <div>
      <Select
        value={String(roster.roster_status.id)}
        onChange={(key) => handleRosterStatusChange(key, roster)}
        aria-label={t('frontend.messages.roster_status_2')}
        variant="secondary"
      >
        <SelectTrigger>
          <SelectValue />
          <SelectIndicator />
        </SelectTrigger>
        <SelectPopover>
          <ListBox items={results || []}>
            {(rs: RhinoRead<'roster_status'>) => (
              <ListBoxItem
                id={String(rs.id)}
                key={rs.id}
                textValue={rs.display_name ?? rs.name}
              >
                {rs.display_name ?? rs.name}
              </ListBoxItem>
            )}
          </ListBox>
        </SelectPopover>
      </Select>
    </div>
  );
};

type PlayerRosterStatusesProps = {
  player: Pick<RhinoRead<'player'>, 'id'>;
};

const PlayerRosterStatuses = ({ player }: PlayerRosterStatusesProps) => {
  const { results: rosters } = useModelIndex('roster', {
    filter: {
      player_id: player.id
    },
    order: ['-team_season.season.starts_on']
  });

  return (
    <div className="flex flex-col gap-1.5">
      {rosters?.map((roster) => (
        <div key={roster.id} className="flex flex-row items-center">
          <div>{roster.team_season.display_name}</div>
          <div className="ml-auto">
            <PlayerRosterStatus roster={roster} />
          </div>
        </div>
      ))}
    </div>
  );
};

export default PlayerRosterStatuses;
